import { BusinessObjectBase } from './base';
import { userTaskSchema } from '@schemas/business-objects/user-task';
import type { Role } from './role';
import type { Tenant } from './tenant';
import type { Token } from './token';
import type { User } from './user';
import type { UserTaskHistory } from './user-task-history';
import type { Id, Json, UserTaskStatus } from '../base-types';
import type { PendingStateUpdate } from './pending-state-update';
import type { ProcessInstance } from './process-instance';
import type { ProcessInstanceHistory } from './process-instance-history';
import type { RoleAssignment } from './role-assignment';

export interface UserTaskData {
  readonly id: Id;
  readonly referenceNumber?: number;
  readonly name?: string;
  readonly userInterface?: string;
  readonly status?: UserTaskStatus;
  readonly data?: Json;
  readonly completedAt?: Date;
  readonly loopIndex?: number;
  readonly resultApplied?: boolean;
  readonly tokenId: Id;
  readonly token?: Token;
  readonly roleId: Id;
  readonly role?: Role;
  readonly userId: Id;
  readonly user?: User;
  readonly userTaskHistories?: UserTaskHistory[];
  readonly tenantId: Id;
  readonly tenant?: Tenant;
  readonly createdAt?: Date;
  readonly updatedAt?: Date;
}

export interface UserTask extends Readonly<UserTaskData> {}
type UserTaskCreateWithChildrenData = Partial<
  Omit<UserTaskData, 'userTaskHistories'>
> & { userTaskHistories?: Partial<UserTaskHistory>[] };

export class UserTask extends BusinessObjectBase {
  static readonly entityName = 'UserTask' as const;
  static readonly plural = 'user-tasks';
  static schema = userTaskSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<UserTask[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new UserTask(data));
  }

  static async findOne(filter?: any): Promise<UserTask | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new UserTask(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<UserTask> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new UserTask(data);
  }

  static async create(data: Omit<UserTaskData, 'id'>): Promise<UserTask> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new UserTask(result);
  }

  static async createMany(
    data: Omit<UserTaskData, 'id'>[],
  ): Promise<UserTask[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new UserTask(item));
  }

  static async createWithChildren(
    data: UserTaskCreateWithChildrenData,
  ): Promise<UserTask> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new UserTask(result);
  }

  static async createManyWithChildren(
    data: UserTaskCreateWithChildrenData[],
  ): Promise<UserTask[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new UserTask(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<UserTaskData>,
  ): Promise<UserTask> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new UserTask(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async claim(userId?: Id): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${UserTask.plural}/${(this as any).id}/claim`;
    return BusinessObjectBase.post<any>(url, { userId });
  }
  async complete(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${UserTask.plural}/${(this as any).id}/complete`;
    return BusinessObjectBase.post<any>(url, options);
  }
  async reassign(userId: Id): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${UserTask.plural}/${(this as any).id}/reassign`;
    return BusinessObjectBase.post<any>(url, { userId });
  }
  async unclaim(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${UserTask.plural}/${(this as any).id}/unclaim`;
    return BusinessObjectBase.post<any>(url, {});
  }
  async updateData(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${UserTask.plural}/${(this as any).id}/update-data`;
    return BusinessObjectBase.post<any>(url, options);
  }
}
